<template>
  <BaseModal
    :heading="$t('treatwell.update_profile.description.header')"
    useForm
    :parentRoute="parentRouteName"
    :loading="queryLoading"
    @submit="submit"
    @close="close"
  >
    <div
      :class="[
        $style.base,
        {
          [$style.smallScreen]: $screen === 's'
        }
      ]"
    >
      <BaseText>
        {{ $t('treatwell.update_profile.description.rules.header') }}
      </BaseText>
      <ul>
        <li
          v-for="i in ruleIndices"
          :key="i"
        >
          <BaseText>
            {{ $t(`treatwell.update_profile.description.rules.${i}`) }}
          </BaseText>
        </li>
      </ul>
      <BaseCard>
        <template v-if="!hasActiveVenue">
          <div :class="$style.form">
            <BaseInput
              v-model="formData.descriptionAtmosphere"
              :label="$t('treatwell.status.salon_description_atmosphere')"
              type="textarea"
              :rows="2"
              v-test="'input-descriptionAtmosphere'"
            />
            <BaseInput
              v-model="formData.descriptionExperience"
              :label="$t('treatwell.status.salon_description_experience')"
              type="textarea"
              :rows="2"
              v-test="'input-descriptionExperience'"
            />
            <BaseInput
              v-model="formData.descriptionSpecialities"
              :label="$t('treatwell.status.salon_description_specialises')"
              type="textarea"
              :rows="2"
              v-test="'input-descriptionSpecialities'"
            />
            <BaseInput
              v-model="formData.descriptionBrandsProducts"
              :label="$t('treatwell.status.salon_description_brands')"
              type="textarea"
              :rows="2"
              v-test="'input-descriptionBrandsProducts'"
            />
            <BaseInput
              v-model="formData.descriptionTransports"
              :label="$t('treatwell.status.salon_description_public_transport')"
              type="textarea"
              :rows="2"
              v-test="'input-descriptionTransports'"
            />
            <BaseInput
              v-model="formData.descriptionExtras"
              :label="$t('treatwell.status.salon_description_extras')"
              type="textarea"
              :rows="2"
              v-test="'input-descriptionExtras'"
            />
          </div>
        </template>
        <template v-else>
          <BaseInput
            v-model="venueDescription"
            :label="$t('treatwell.status.salon_description')"
            type="textarea"
            :rows="12"
            v-test="'input-venueDescription'"
          />
        </template>
      </BaseCard>
    </div>
    <template #footer>
      <BaseButton
        submitForm
        :disabled="isSubmitting"
        v-test="'salon-description-submit'"
      >
        {{ $t('global.actions.submit') }}
      </BaseButton>
    </template>
  </BaseModal>
</template>

<script
  setup
  lang="ts"
>
import { useMutation, useQuery } from '@vue/apollo-composable';
import gql from 'graphql-tag';
import { storeToRefs } from 'pinia';
import { computed, reactive, ref } from 'vue';
import { useI18n } from 'vue-i18n';
import { useRouter } from 'vue-router';
import { flash, modal } from '@/helpers/ui';
import { useCompanyStore } from '@/stores/company';
import { useTreatwellStore } from '@/stores/treatwell';
import { GET_INTEGRATION_REQUEST_DESCRIPTION } from '../graphql';

const props = defineProps<{
  parentRouteName?: string;
}>();

const emit = defineEmits(['close', 'event']);

const router = useRouter();

const UPSERT_CHANGE_REQUEST = gql`
  mutation treatwellUpsertChangeRequest($input: UpsertChangeRequestInput!) {
    treatwellUpsertChangeRequest(input: $input) {
      treatwellIntegrationRequest {
        id
      }
    }
  }
`;

const UPDATE_VENUE_DESCRIPTION = gql`
  mutation updateTreatwellVenue($input: UpdateTreatwellVenueInput!) {
    updateTreatwellVenue(input: $input) {
      treatwellVenue {
        id
        description
      }
      errors
    }
  }
`;

const formData = reactive({
  descriptionAtmosphere: '',
  descriptionExperience: '',
  descriptionSpecialities: '',
  descriptionBrandsProducts: '',
  descriptionTransports: '',
  descriptionExtras: '',
});

const { treatwellStatus, hasActiveVenue, getTreatwellStatus } =
  useTreatwellStore();
const { company } = storeToRefs(useCompanyStore());

const venueDescription = ref(company.value?.treatwellVenue?.description ?? '');

const ruleIndices = computed(() => {
  const indices = [1, 2, 3, 4];
  return hasActiveVenue ? indices.slice(1) : indices;
});

const { onResult, loading: queryLoading } = useQuery(
  GET_INTEGRATION_REQUEST_DESCRIPTION,
  null,
  () => ({
    enabled: !hasActiveVenue,
  }),
);

onResult(
  ({
    data: {
      treatwellCurrentIntegrationRequest: { changeRequest },
    },
  }) => {
    if (changeRequest) {
      Object.keys(formData).forEach((key) => {
        const formKey = key as keyof typeof formData;
        if (changeRequest[formKey]) {
          formData[formKey] = changeRequest[formKey];
        }
      });
    }
  },
);

const { mutate: upsertChangeRequest, loading: upsertLoading } = useMutation(
  UPSERT_CHANGE_REQUEST,
  {
    variables: {
      input: {
        salonDescription: formData,
      },
    },
  },
);

const { mutate: updateVenueDescription, loading: updateLoading } = useMutation(
  UPDATE_VENUE_DESCRIPTION,
);

const isSubmitting = computed(() => upsertLoading.value || updateLoading.value);

const close = () => {
  props.parentRouteName
    ? router.push({
        name: props.parentRouteName,
      })
    : emit('close');
};

const { t } = useI18n();

const submit = () => {
  if (hasActiveVenue && company.value?.treatwellVenue) {
    updateVenueDescription({
      input: {
        description: venueDescription.value,
      },
    }).then((result) => {
      if (!result) return;

      const errors = result.data?.updateTreatwellVenue.errors;
      if (errors?.length) {
        flash(t('global.error_generic'));
      } else {
        emit('event', 'provide_description');
        flash(t('global.flash.treatwell_integration_updated'));
        getTreatwellStatus();
        close();
      }
    });
  } else if (!hasActiveVenue) {
    upsertChangeRequest().then(() => {
      flash(t('global.flash.treatwell_integration_updated'));
      treatwellStatus.descriptionProvided = true;
      close();
    });
  }
};
</script>

<style
  lang="scss"
  module
>
.base {
  display: flex;
  flex-direction: column;
  gap: $spacing;
}

.form {
  .base:not(.smallScreen) & {
    display: flex;
    flex-wrap: wrap;
    gap: $spacing;

    & > * {
      width: calc(50% - #{$spacing * 0.5});
    }
  }
}
</style>
